import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import api from "../../utils/api";
import { trackAjpPlanGoogleAdsConversion, trackFunnelEvent, trackFunnelEventOnce } from "../../utils/funnelTracking";
import * as ajpPageRegistry from "../../data/ajpPageRegistry";
import JourneyAlertConversion from "./JourneyAlertConversion";
import { readAjpPlanSession, restoredPlanPayload, saveAjpPlanSession } from "../../utils/ajpPlanSession";
import "./AjpPlannerEngine.css";
import "./AjpPlannerDetails.css";

const SITE = "https://www.airportjourneyplanner.com";
const { getAjpAirport } = ajpPageRegistry;

const statusClass = (value) => {
  const text = String(value || "checking").toLowerCase();
  if (/red|disrupt|severe|delay/.test(text)) return "bad";
  if (/amber|watch|moderate|busy/.test(text)) return "watch";
  if (/green|good|normal|clear|on time/.test(text)) return "good";
  return "checking";
};

const statusLabel = (value) => {
  const kind = statusClass(value);
  if (kind === "bad") return "Disrupted";
  if (kind === "watch") return "Watch";
  if (kind === "good") return "Good";
  return "Checking…";
};

const money = (value) => Number.isFinite(Number(value)) ? `£${Number(value).toFixed(2)}` : "Check plan";
const minutes = (value) => Number.isFinite(Number(value)) ? `${Math.round(Number(value))} min` : "Check plan";
const clock = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });
};
const localDateTimeValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(date).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

function DetailHeader({ icon, title, status, summary, tone }) {
  const value = status?.level || status?.label;
  return <div className={`ajp-engine__panel-header is-${tone || "default"}`}><div className="ajp-engine__detail-head"><h3>{icon} {title}</h3><span className={`ajp-engine__badge is-${statusClass(value)}`}>{statusLabel(value)}</span></div>{summary ? <p className="ajp-engine__detail-summary">{summary}</p> : null}</div>;
}

function DetailRows({ rows, empty = "Data is being prepared." }) {
  if (!rows?.length) return <p className="ajp-engine__empty">{empty}</p>;
  return <div className="ajp-engine__detail-rows">{rows.map((row, index) => <article key={row.key || `${row.title}-${index}`}><div><strong>{row.title}</strong>{row.meta ? <small>{row.meta}</small> : null}</div><span className={`ajp-engine__mini-status is-${statusClass(row.status)}`}>{row.statusText || statusLabel(row.status)}</span>{row.detail ? <p>{row.detail}</p> : null}</article>)}</div>;
}

function extractCards(result) {
  const root = result?.data || result || {};
  const projection = root?.ui_projection || result?.ui_projection || {};
  const cards = projection?.cards || {};
  const modes = root?.modes || {};
  const find = (names) => names.map((name) => cards[name] || modes[name]).find(Boolean) || {};
  const rail = find(["rail", "train"]);
  const taxi = find(["taxi"]);
  const drive = find(["self_drive", "drive"]);
  const durationOf = (card) => card?.duration_mins ?? card?.total_duration_mins ?? card?.door_to_terminal_mins ?? card?.road?.duration_mins ?? card?.road?.live_duration_mins ?? card?.best_candidate?.journeys?.[0]?.duration_mins ?? card?.best_candidate?.journeys?.[0]?.duration_minutes ?? card?.best_candidate?.journeys?.[0]?.total_duration_minutes;
  const fareOf = (card) => card?.fare_gbp ?? card?.cost_gbp ?? card?.price_gbp ?? card?.fare?.total_gbp ?? card?.best_candidate?.fare?.total_gbp;
  const detailOf = (card, fallback) => card?.route_summary || card?.detail || card?.insight || card?.summary || card?.road?.route_name || fallback;
  return [
    { key: "rail", icon: "🚆", name: "Rail", tone: "rail", status: rail?.status, fare: fareOf(rail), duration: durationOf(rail), detail: detailOf(rail, "Station and transfer details are shown in your plan.") },
    { key: "taxi", icon: "🚕", name: "Taxi", tone: "taxi", status: taxi?.status, fare: fareOf(taxi), duration: durationOf(taxi), detail: detailOf(taxi, "Door-to-terminal route with live road timing.") },
    { key: "drive", icon: "🚙", name: "Self-drive", tone: "drive", status: drive?.status, fare: fareOf(drive), duration: durationOf(drive), detail: detailOf(drive, "Road, fuel, parking and terminal transfer context.") },
  ];
}

export default function AjpPlannerEngine({ page }) {
  const airport = getAjpAirport(page?.airportCode);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ postcode: "", flight: "", departure: "", adults: 2, children: 0 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [postcodeSuggestions, setPostcodeSuggestions] = useState([]);
  const [flightSuggestions, setFlightSuggestions] = useState([]);
  const [postcodeFocused, setPostcodeFocused] = useState(false);
  const [flightFocused, setFlightFocused] = useState(false);
  const [flightLookupComplete, setFlightLookupComplete] = useState(false);
  const planStartedRef = useRef(false);

  const trackingContext = {
    brand: "planner",
    tracking_source: page?.type === "city-airport" ? "ajp_route_page" : "ajp_airport_page",
    page_type: page?.type,
    page_key: page?.key,
    airport: airport?.code,
    city: page?.city,
  };

  useEffect(() => {
    if (!airport) return;
    const saved = readAjpPlanSession(airport.code);
    if (!saved?.journeyId) return;
    setForm((current) => ({ ...current, ...(saved.form || {}) }));
    if (saved.planResponse) {
      setResult(saved.planResponse);
      trackFunnelEvent("ajp_plan_restored", { ...trackingContext, ajp_journey_id: saved.journeyId, restore_source: "browser_session" });
    }
    let cancelled = false;
    api.get(`/api/ajp/v2/session/${saved.journeyId}`).then((response) => {
      if (cancelled || !response?.data?.success) return;
      setForm((current) => ({ ...current, ...(saved.form || {}) }));
      setResult(restoredPlanPayload(response.data.data));
      if (!saved.planResponse) trackFunnelEvent("ajp_plan_restored", { ...trackingContext, ajp_journey_id: saved.journeyId, restore_source: "backend_session" });
    }).catch(() => {});
    return () => { cancelled = true; };
  // Restore is intentionally attempted once for the mounted airport page.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airport?.code]);

  useEffect(() => {
    if (!page || !airport) return;
    trackFunnelEventOnce("landing_view", trackingContext);
  // Each generated page path mounts its own engine instance.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page?.path, airport?.code]);

  const markPlanStarted = (fieldName) => {
    if (planStartedRef.current) return;
    planStartedRef.current = true;
    trackFunnelEvent("ajp_plan_start", { ...trackingContext, field_name: fieldName });
  };

  useEffect(() => {
    if (!airport) return undefined;
    let active = true;
    const load = async () => {
      try {
        const response = await api.get(`/api/open/airport-intelligence-summary?airports=${airport.code}`);
        if (active) setSummary(response?.data?.airports?.[0] || null);
      } catch (_error) {
        if (active) setSummary(null);
      }
    };
    load();
    const timer = window.setInterval(load, 5 * 60 * 1000);
    return () => { active = false; window.clearInterval(timer); };
  }, [airport]);

  useEffect(() => {
    const query = form.postcode.trim();
    if (!postcodeFocused || query.length < 2) {
      setPostcodeSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const response = await api.get("/api/postcodes/suggest", { params: { q: query, limit: 8 } });
        if (cancelled) return;
        const suggestions = (response?.data?.suggestions || []).map((item) => String(item || "").toUpperCase()).filter(Boolean);
        setPostcodeSuggestions([...new Set(suggestions)].slice(0, 8));
      } catch (_error) {
        if (!cancelled) setPostcodeSuggestions([]);
      }
    }, 180);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [form.postcode, postcodeFocused]);

  useEffect(() => {
    const query = form.flight.replace(/\s+/g, "").toUpperCase();
    setFlightLookupComplete(false);
    if (!flightFocused || query.length < 2 || !airport) {
      setFlightSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const response = await api.get("/api/ajp/v2/flights/suggest", { params: { q: query, limit: 8, direction: "Departure", airport_code: airport.code } });
        if (!cancelled) setFlightSuggestions((response?.data?.suggestions || []).map((item) => String(item || "").toUpperCase()).filter(Boolean));
      } catch (_error) {
        if (!cancelled) setFlightSuggestions([]);
      } finally {
        if (!cancelled) setFlightLookupComplete(true);
      }
    }, 180);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [airport, flightFocused, form.flight]);

  useEffect(() => {
    const flightNumber = form.flight.replace(/\s+/g, "").toUpperCase();
    if (!airport || flightNumber.length < 3) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const response = await api.get("/api/ajp/v2/flights/schedules", { params: { flight_number: flightNumber, direction: "Departure", airport_code: airport.code } });
        const first = response?.data?.schedules?.[0];
        const anchor = first?.scheduled_anchor_utc || first?.scheduled_departure_utc || first?.scheduled_time_utc;
        if (!cancelled && anchor) setForm((current) => current.flight.replace(/\s+/g, "").toUpperCase() === flightNumber ? { ...current, departure: localDateTimeValue(anchor) } : current);
      } catch (_error) {
        // The date/time input remains available for manual entry.
      }
    }, 240);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [airport, form.flight]);

  const cards = useMemo(() => extractCards(result), [result]);
  const canonical = page?.path ? `${SITE}${page.path}` : SITE;
  const overall = summary?.overall?.label || summary?.hero_status?.overall?.label || "Checking";
  const detailed = summary?.detailed_status || {};
  const signals = [
    ["Flights", summary?.hero_status?.flights?.level || summary?.hero_status?.flights?.label],
    ["Roads", summary?.hero_status?.road?.level || summary?.hero_status?.road?.label],
    ["Rail", summary?.hero_status?.rail?.level || summary?.hero_status?.rail?.label],
    ["Weather", summary?.hero_status?.weather?.level || summary?.hero_status?.weather?.label],
    ["Queues", summary?.hero_status?.queues?.level || summary?.hero_status?.queues?.label],
    ["Terminal", summary?.hero_status?.terminals?.level || summary?.hero_status?.terminal?.level || summary?.hero_status?.terminals?.label || summary?.hero_status?.terminal?.label],
  ];
  const flightRows = [
    ...(detailed?.flights?.recent_arrivals || []).slice(0, 2).map((flight) => ({ key: `a-${flight.id}`, title: `${flight.flight_number || "Flight"} arrival`, meta: flight.origin_airport_name || "Arrival", detail: `${flight.status_text || "Status updating"} · ${flight.terminal && flight.terminal !== "-" ? `Terminal ${flight.terminal} · ` : ""}${clock(flight.anchor_utc)}`, status: flight.status_label })),
    ...(detailed?.flights?.recent_departures || []).slice(0, 2).map((flight) => ({ key: `d-${flight.id}`, title: `${flight.flight_number || "Flight"} departure`, meta: flight.destination_airport_name || "Departure", detail: `${flight.status_text || "Status updating"} · ${flight.terminal && flight.terminal !== "-" ? `Terminal ${flight.terminal} · ` : ""}${clock(flight.anchor_utc)}`, status: flight.status_label })),
  ];
  const roadRows = (detailed?.road?.corridors || []).slice(0, 4).map((corridor) => ({ key: corridor.key, title: corridor.name, meta: Number.isFinite(Number(corridor.live_minutes)) ? `${corridor.live_minutes} min live` : null, detail: Number.isFinite(Number(corridor.delay_minutes)) ? `${corridor.delay_minutes > 0 ? `+${corridor.delay_minutes} min vs usual` : "Normal journey time"}${corridor.segments?.length ? ` · ${corridor.segments.map((segment) => segment.name).join(" → ")}` : ""}` : corridor.reason, status: corridor.level || corridor.label }));
  const railRows = (detailed?.rail?.observations || []).slice(0, 4).map((rail) => ({ key: rail.label, title: rail.label, meta: rail.service_count != null ? `${rail.service_count} services checked` : null, detail: `${rail.reason || "Status updating"}${rail.latest_departure?.operator_name ? ` · ${rail.latest_departure.operator_name}` : ""}`, status: rail.level || rail.label }));
  const terminalRows = (detailed?.terminals?.terminals || summary?.preview?.terminal_pressure || []).slice(0, 4).map((terminal) => {
    const terminalName = String(terminal.terminal || "");
    const title = /^(North|South)$/i.test(terminalName)
      ? `${terminalName} terminal`
      : terminalName === "Main"
      ? "Main terminal"
      : `Terminal ${terminalName}`;
    return { key: terminal.terminal, title, meta: terminal.flights != null ? `${terminal.flights} flights` : null, detail: terminal.reason || `${terminal.cancelled || 0} cancelled · ${terminal.delayed_30 || 0} delayed 30m+`, status: terminal.level || terminal.label };
  });
  const queueRows = (detailed?.queues?.terminals || summary?.preview?.terminal_queues || []).slice(0, 4).map((queue) => ({ key: queue.terminal || queue.label, title: queue.terminal ? `Terminal ${queue.terminal}` : queue.label || "Queue", detail: queue.reason || queue.headline, status: queue.level || queue.label }));

  useEffect(() => {
    if (!page || !airport) return;
    document.title = page.title;
    const ensureHeadElement = (selector, create) => {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = create();
        element.setAttribute("data-ajp-engine", "true");
        document.head.appendChild(element);
      }
      return element;
    };
    const canonicalElement = ensureHeadElement('link[rel="canonical"]', () => {
      const element = document.createElement("link");
      element.rel = "canonical";
      return element;
    });
    canonicalElement.href = canonical;
    const descriptionElement = ensureHeadElement('meta[name="description"]', () => {
      const element = document.createElement("meta");
      element.name = "description";
      return element;
    });
    descriptionElement.content = page.description;
  }, [airport, canonical, page?.description, page?.title]);

  if (!page || !airport) return null;

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    trackFunnelEvent("ajp_plan_submit", {
      ...trackingContext,
      has_postcode: Boolean(form.postcode.trim()),
      has_flight: Boolean(form.flight.trim()),
      has_departure_time: Boolean(form.departure),
    });
    if (!form.postcode.trim() || !form.departure) {
      setError("Enter your starting postcode and the flight departure date and time.");
      trackFunnelEvent("ajp_plan_failed", { ...trackingContext, failure_reason: "missing_required_fields" });
      return;
    }
    setLoading(true);
    try {
      const response = await api.post("/api/ajp/v2/plan", {
        airport_code: airport.code,
        pickup_postcode: form.postcode,
        flight_number: form.flight || "MANUAL",
        flight_anchor_utc: new Date(form.departure).toISOString(),
        adults: Number(form.adults),
        children: Number(form.children),
        preferred_modes: ["rail", "taxi", "self_drive"],
      });
      const nextResult = response?.data || null;
      setResult(nextResult);
      saveAjpPlanSession({ journeyId: nextResult?.data?.journey_id || nextResult?.journey_id, airportCode: airport.code, form, planResponse: nextResult });
      trackFunnelEvent("ajp_plan_created", {
        ...trackingContext,
        journey_id: nextResult?.data?.journey_id || nextResult?.journey_id,
        flight_number: form.flight || "MANUAL",
      });
      trackAjpPlanGoogleAdsConversion(nextResult?.data?.journey_id || nextResult?.journey_id);
      extractCards(nextResult).forEach((card) => trackFunnelEvent("ajp_transport_result_view", {
        ...trackingContext,
        mode: card.key === "drive" ? "self_drive" : card.key,
        status: statusClass(card.status),
        fare_gbp: card.fare,
        duration_mins: card.duration,
      }));
      window.setTimeout(() => document.getElementById("ajp-generated-comparison")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "We could not build the plan. Check the details and try again.");
      trackFunnelEvent("ajp_plan_failed", {
        ...trackingContext,
        failure_reason: requestError?.response?.data?.error || requestError?.code || "request_failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const trackTransportClick = (mode, destination) => trackFunnelEvent(`ajp_${mode}_cta_click`, {
    ...trackingContext,
    mode,
    destination,
  });
  const requestBooking = () => {
    trackTransportClick("taxi", "support_chat");
    window.dispatchEvent(new CustomEvent("ccg:open-support-chat", { detail: { flow: "enquiry" } }));
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: page.title,
    description: page.description,
    url: canonical,
    about: { "@type": "Airport", name: airport.fullName, iataCode: airport.code },
  };

  return (
    <main className="ajp-engine">
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.description} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.description} />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <section className="ajp-engine__hero" style={{ "--ajp-airport-accent": airport.accent }}>
        <div className="ajp-engine__wrap">
          <p className="ajp-engine__eyebrow">● Live {airport.code} journey planning</p>
          <h1>{page.h1}</h1>
          <p className="ajp-engine__lead">{page.intro}</p>
          <div className="ajp-engine__trust">Prepared airport snapshot · Flight-timed comparison · No booking obligation</div>

          <form className="ajp-engine__form" onSubmit={submit} onFocusCapture={(event) => markPlanStarted(event.target?.name || event.target?.type || "planner_form")}>
            <label className="ajp-engine__field"><span>Starting postcode</span><input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} onFocus={() => setPostcodeFocused(true)} onBlur={() => window.setTimeout(() => setPostcodeFocused(false), 120)} autoComplete="postal-code" placeholder={page.originHint || "e.g. SW1A 1AA"} />{postcodeFocused && postcodeSuggestions.length ? <div className="ajp-engine__suggestions" role="listbox">{postcodeSuggestions.map((postcode) => <button key={postcode} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => { setForm({ ...form, postcode }); setPostcodeSuggestions([]); setPostcodeFocused(false); }}>{postcode}</button>)}</div> : null}</label>
            <label className="ajp-engine__field"><span>Flight number <small>optional</small></span><input value={form.flight} onChange={(e) => { const flight = e.target.value.toUpperCase(); setForm((current) => ({ ...current, flight, departure: "" })); setFlightSuggestions([]); setFlightLookupComplete(false); }} onFocus={() => setFlightFocused(true)} onBlur={() => window.setTimeout(() => setFlightFocused(false), 120)} autoComplete="off" placeholder="e.g. BA123" />{flightFocused && flightSuggestions.length ? <div className="ajp-engine__suggestions" role="listbox">{flightSuggestions.map((flight) => <button key={flight} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => { setForm((current) => ({ ...current, flight, departure: "" })); setFlightSuggestions([]); setFlightLookupComplete(false); setFlightFocused(false); }}>{flight}</button>)}</div> : null}{flightFocused && flightLookupComplete && form.flight.replace(/\s+/g, "").length >= 2 && !flightSuggestions.length ? <small className="ajp-engine__manual-note">No matching future flight found. You can enter the flight number and date/time manually.</small> : null}</label>
            <label className="ajp-engine__field ajp-engine__field--time"><span>Flight departure date &amp; time</span><input type="datetime-local" value={form.departure} onChange={(e) => setForm({ ...form, departure: e.target.value })} /></label>
            <button type="submit" disabled={loading}>{loading ? "Building plan…" : "Compare my journey →"}</button>
          </form>
          {error ? <p className="ajp-engine__error" role="alert">{error}</p> : null}
          {!result ? <div style={{ marginTop: 12 }}><JourneyAlertConversion airportCode={airport.code} postcode={form.postcode} flightNumber={form.flight} flightAnchorUtc={form.departure} placement={`${page.type}-hero`} label="Get disruption alerts for this journey" /></div> : null}
        </div>
      </section>

      {result ? <section id="ajp-generated-comparison" className="ajp-engine__wrap ajp-engine__section">
        <p className="ajp-engine__eyebrow">Your comparison</p><h2>Three ways to reach {airport.name}</h2>
        <div className="ajp-engine__cards">{cards.map((card) => <article className={`ajp-engine__card is-${card.tone}`} key={card.key}><div className="ajp-engine__card-header"><h3>{card.icon} {card.name}</h3><span className={`ajp-engine__badge is-${statusClass(card.status)}`}>{statusLabel(card.status)}</span></div><dl><div><dt>🎟️ Fare</dt><dd>{money(card.fare)}</dd></div><div><dt>⏱️ Duration</dt><dd>{minutes(card.duration)}</dd></div><div><dt>🧭 Details</dt><dd>{card.detail}</dd></div></dl>{card.key === "rail" ? <a href="https://www.nationalrail.co.uk/" target="_blank" rel="noreferrer" onClick={() => trackTransportClick("rail", "national_rail")}>Check fares & tickets ↗</a> : card.key === "taxi" ? <button type="button" onClick={requestBooking}>Request a booking</button> : <a href={airport.parkingUrl} target="_blank" rel="noreferrer" onClick={() => trackTransportClick("self_drive", "official_airport_parking")}>Book airport parking ↗</a>}<JourneyAlertConversion compact airportCode={airport.code} postcode={form.postcode} flightNumber={form.flight} flightAnchorUtc={form.departure} travelMode={card.key === "drive" ? "self_drive" : card.key} journeyId={result?.data?.journey_id || result?.journey_id} placement={`${page.type}-${card.key}-result`} label={`Alert me about ${card.name.toLowerCase()} disruption`} /></article>)}</div>
        <div style={{ marginTop: 16 }}><JourneyAlertConversion airportCode={airport.code} postcode={form.postcode} flightNumber={form.flight} flightAnchorUtc={form.departure} journeyId={result?.data?.journey_id || result?.journey_id} placement={`${page.type}-result`} label="Get alerts if this airport journey changes" /></div>
        <p className="ajp-engine__small">Indicative journey information only. Confirm tickets, parking, flight and terminal details with the relevant provider before travel.</p>
      </section> : null}

      <section className="ajp-engine__wrap ajp-engine__section">
        <div className="ajp-engine__heading-row"><div><p className="ajp-engine__eyebrow">Latest prepared snapshot</p><h2>{airport.name} conditions</h2></div><span className={`ajp-engine__badge is-${statusClass(overall)}`}>{statusLabel(overall)}</span></div>
        <div className="ajp-engine__signals">{signals.map(([label, value]) => <article key={label}><span>{label}</span><strong className={`is-${statusClass(value)}`}>{statusLabel(value)}</strong></article>)}</div>
      </section>

      {summary ? <section className="ajp-engine__wrap ajp-engine__details" aria-label={`${airport.name} detailed live status`}>
        <article className="ajp-engine__detail-panel"><DetailHeader icon="✈️" title="Flights" tone="flights" status={detailed.flights || summary?.hero_status?.flights} summary={detailed?.flights?.headline || summary?.hero_status?.flights?.headline} /><DetailRows rows={flightRows} empty="Recent visible flights are being prepared." /></article>
        <article className="ajp-engine__detail-panel"><DetailHeader icon="🚗" title="Road corridors" tone="roads" status={detailed.road || summary?.hero_status?.road} summary={detailed?.road?.headline || summary?.hero_status?.road?.headline} /><DetailRows rows={roadRows} empty="Road corridor checks are being prepared." /></article>
        <article className="ajp-engine__detail-panel"><DetailHeader icon="🚆" title="Rail and stations" tone="rail" status={detailed.rail || summary?.hero_status?.rail} summary={detailed?.rail?.headline || summary?.hero_status?.rail?.headline} /><DetailRows rows={railRows} empty="Rail board and service checks are being prepared." /></article>
        <article className="ajp-engine__detail-panel"><DetailHeader icon="🌤️" title="Weather" tone="weather" status={detailed.weather || summary?.hero_status?.weather} summary={detailed?.weather?.reason || summary?.hero_status?.weather?.headline} /><DetailRows rows={detailed?.weather?.current ? [{ key: "weather", title: detailed.weather.current.weather_text || "Current weather", meta: `${Math.round(Number(detailed.weather.current.temp_c))}°C`, detail: `Wind ${Number(detailed.weather.current.wind_mps || 0).toFixed(1)} m/s · observed ${clock(detailed.weather.current.observed_at)}`, status: detailed.weather.level }] : []} empty="Weather observation is being prepared." /></article>
        <article className="ajp-engine__detail-panel"><DetailHeader icon="🛫" title="Terminal pressure" tone="terminal" status={detailed.terminals || summary?.hero_status?.terminals} summary={detailed?.terminals?.headline || summary?.hero_status?.terminals?.headline} /><DetailRows rows={terminalRows} empty="Terminal pressure is being prepared." /></article>
        <article className="ajp-engine__detail-panel"><DetailHeader icon="👥" title="Queue snapshot" tone="queues" status={detailed.queues || summary?.hero_status?.queues} summary={detailed?.queues?.headline || summary?.hero_status?.queues?.headline} /><DetailRows rows={queueRows} empty={detailed?.queues?.availability === "unavailable" ? "Live queue data is not available for this airport." : "Queue checks are being prepared."} /></article>
      </section> : null}

      <section className="ajp-engine__wrap ajp-engine__section ajp-engine__content">
        <article><h2>{page.type === "city-airport" ? `${page.city} to ${airport.name}` : `Getting to ${airport.name}`}</h2><p>{page.routeDetail || `${airport.roads.join(", ")} are important road approaches to monitor before travelling.`}</p>{page.typicalDuration ? <p><strong>Typical road duration:</strong> {page.typicalDuration}. Recheck close to departure.</p> : null}</article>
        <article><h2>Rail and terminal</h2><p>{airport.rail}</p><p>{airport.terminals}. Confirm the operating terminal with your airline before leaving.</p></article>
        <article><h2>Parking and drop-off</h2><p>Compare the full journey, including parking transfer or drop-off walking time—not only the driving time.</p><div className="ajp-engine__links"><a href={airport.parkingUrl} target="_blank" rel="noreferrer">Official parking ↗</a>{airport.dropoffPath ? <Link to={airport.dropoffPath}>Drop-off calculator</Link> : null}</div></article>
      </section>
    </main>
  );
}
